// src/components/studio/steps/Step2Letter.tsx
import { motion } from "framer-motion";
import { Lock, Sparkles } from "lucide-react";
import { PrivacyModeSelector } from "../PrivacyModeSelector";

interface Step2LetterProps {
  customization: any;
  setCustomization: (c: any) => void;
  validation: any;
}

const MAX_COMFORTABLE_LENGTH = 1400;

export default function Step2Letter({
  customization,
  setCustomization,
  validation,
}: Step2LetterProps) {
  const recipientName = customization.recipientName?.trim();
  const dearText = recipientName ? `Dear ${recipientName},` : "Dear someone special,";
  const messageLength = customization.message?.length || 0;

  const updateCustomization = (updates: Record<string, unknown>) => {
    setCustomization({
      ...customization,
      ...updates,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="
        flex
        h-full
        min-h-0
        w-full
        flex-col
        overflow-hidden
      "
    >
      {/* TOP BAR */}
      <div
        className="
          flex
          flex-shrink-0
          flex-col
          gap-2
          pb-3
          md:flex-row
          md:items-end
          md:justify-between
          md:pb-4
        "
      >
        <div className="min-w-0">
          <p className="mb-1.5 text-[8px] font-black uppercase tracking-[0.34em] text-white/30">
            Intentional Expression
          </p>

          <div className="flex min-w-0 items-center gap-3">
            <h2
              className="
                truncate
                text-[18px]
                font-light
                leading-none
                tracking-[-0.055em]
                text-white/90
                md:text-[18px]
              "
            >
              Speak from the heart.
            </h2>

            {validation.isStep2Valid && (
              <div className="hidden flex-shrink-0 rounded-full border border-primary/20 bg-primary/10 px-3 py-1.5 text-[8px] font-black uppercase tracking-[0.2em] text-primary/80 sm:block">
                Ready
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center justify-between gap-2 md:justify-end">
          <div
            className="
              inline-flex
              h-9
              items-center
              gap-2
              rounded-full
              border
              border-white/10
              bg-white/[0.045]
              px-3
              text-[8px]
              font-black
              uppercase
              tracking-[0.18em]
              text-white/45
              backdrop-blur-xl
              md:h-10
              md:px-4
              md:text-[9px]
            "
          >
            <Sparkles size={12} className="text-primary/70" />
            <span>{messageLength}</span>
            <span className="hidden text-white/25 sm:inline">/ {MAX_COMFORTABLE_LENGTH}</span>
          </div>

          {/* <PrivacyModeSelector
            privacyMode={customization.privacyMode || "standard_private"}
            requiresPasscode={Boolean(customization.requiresPasscode)}
            passcode={customization.passcode || ""}
            deleteAfterOpen={Boolean(customization.deleteAfterOpen)}
            onChange={(updates) => updateCustomization(updates)}
          /> */}
        </div>
      </div>

      {/* FIT-TO-HEIGHT CONTENT */}
      <section
        className="
          grid
          min-h-0
          flex-1
          grid-cols-1
          gap-3
          overflow-hidden
          xl:grid-cols-[minmax(0,1fr)_238px]
        "
      >
        {/* LETTER PAPER */}
        <div
          className="
            relative
            flex
            min-h-0
            flex-col
            overflow-hidden
            rounded-[24px]
            border
            border-black/5
            bg-[#efe7dc]
            shadow-[0_24px_70px_rgba(0,0,0,0.38)]
            md:rounded-[32px]
          "
        >
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage:
                "url('https://www.transparenttextures.com/patterns/paper-fibers.png')",
            }}
          />

          <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-white/45 to-transparent pointer-events-none" />

          <div
            className="absolute inset-0 opacity-[0.05] pointer-events-none"
            style={{
              backgroundImage: "linear-gradient(#000 1px, transparent 1px)",
              backgroundSize: "100% 38px",
              marginTop: "78px",
            }}
          />

          <div className="relative flex h-full min-h-0 flex-col">
            <div className="flex-shrink-0 px-5 pt-5 md:px-8 md:pt-7">
              <p className="font-serif text-[13px] text-black italic text-black/42 md:text-[15px]">
                {dearText}
              </p>
            </div>

            <div className="min-h-0 flex-1 px-5 pb-3 pt-2 md:px-8 md:pb-5">
              <textarea
                value={customization.message || ""}
                onChange={(e) => updateCustomization({ message: e.target.value })}
                placeholder="Some feelings deserve more than a text message..."
                className="
                  h-full
                  min-h-0
                  w-full
                  resize-none
                  border-none
                  bg-transparent
                  font-serif
                  text-[19px]
                  italic
                  leading-[1.8]
                  tracking-[0.005em]
                  text-[#2f261d]
                  outline-none
                  placeholder:text-black/18
                  md:text-[24px]
                  md:leading-[1.75]
                  2xl:text-[26px]
                  [&::-webkit-scrollbar]:hidden
                "
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  scrollbarWidth: "none",
                }}
              />
            </div>

            <div
              className="
                flex
                flex-shrink-0
                items-center
                gap-2
                border-t
                border-black/[0.06]
                bg-black/[0.025]
                px-5
                py-2.5
                text-[8px]
                font-black
                uppercase
                tracking-[0.2em]
                text-black/35
                md:px-8
              "
            >
              <Lock size={11} />
              <span>Private words deserve care.</span>
              <span className="hidden text-black/25 2xl:inline">
                LRNSY does not use private letters for advertising profiling.
              </span>
            </div>
          </div>
        </div>

        {/* DESKTOP SIDE PANEL */}
        <aside
          className="
            hidden
            min-h-0
            overflow-hidden
            xl:flex
            xl:flex-col
            xl:gap-3
          "
        >
          <div
            className="
              flex-shrink-0
              rounded-[22px]
              border
              border-white/10
              bg-white/[0.045]
              p-4
              backdrop-blur-xl
            "
          >
            <p className="text-[8px] font-black uppercase tracking-[0.28em] text-white/30">
              Writing guide
            </p>

            <h3 className="mt-2 text-[17px] font-light leading-tight tracking-[-0.045em] text-white/86">
              Keep it honest.
            </h3>

            <p className="mt-2 text-[12px] leading-5 text-white/43">
              Write like you are speaking directly to them. Simple and sincere is enough.
            </p>
          </div>

          <div
            className="
    flex-shrink-0
    overflow-hidden
    rounded-[22px]
    border
    border-white/10
    bg-white/[0.035]
    p-4
    backdrop-blur-xl
  "
          >
            <p className="text-[8px] font-black uppercase tracking-[0.28em] text-white/30">
              Privacy posture
            </p>

            <div className="mt-3 grid gap-2">
              <MiniStatus
                label="Encryption"
                value={
                  customization.privacyMode === "ultra_private"
                    ? "Ultra Private"
                    : "Standard Private"
                }
              />
              <MiniStatus
                label="Passcode"
                value={customization.requiresPasscode ? "Required" : "Optional"}
              />
              <MiniStatus
                label="Opening"
                value={customization.deleteAfterOpen ? "Delete once" : "Keep available"}
              />
            </div>
          </div>
        </aside>
      </section>

      {/* TABLET / MOBILE cIPS */}
      <div
        className="flex flex-shrink-0 gap-2 overflow-x-auto pt-3 xl:hidden
                  [&::-webkit-scrollbar]:hidden
      "
      >
        <MobileChip
          label={
            customization.privacyMode === "ultra_private" ? "Ultra Private" : "Standard Private"
          }
        />
        <MobileChip label={customization.requiresPasscode ? "Passcode on" : "Passcode optional"} />
        <MobileChip
          label={customization.deleteAfterOpen ? "Deletes after open" : "Reusable link"}
        />
      </div>
    </motion.div>
  );
}

function MiniStatus({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-3">
      <p className="text-[7px] font-black uppercase tracking-[0.24em] text-white/28">{label}</p>
      <p className="mt-1 truncate text-[13px] text-white/68">{value}</p>
    </div>
  );
}

function MobileChip({ label }: { label: string }) {
  return (
    <span
      className="
        flex-shrink-0
        rounded-full
        border
        border-white/10
        bg-white/[0.045]
        px-3
        py-2
        text-[8px]
        font-black
        uppercase
        tracking-[0.18em]
        text-white/45
      "
    >
      {label}
    </span>
  );
}
